import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Field } from "./InvoiceForm";
import { SaleExtras, extraForForm, extrasTotal, gbp2 } from "./SaleExtras";
import { CollectionDelivery } from "./CollectionDelivery";
import { extraFromPreset, useExtraPresets } from "./UsualExtras";

export const SALE_STATUSES = ["Agreed", "Completed", "Cancelled"];
const NUMBER_FIELDS = ["mileage", "keys", "price", "deposit"];
const OPTIONAL_INTS = ["mileage", "keys"];

export const EMPTY_SALE = {
  sale_date: new Date().toISOString().slice(0, 10), status: "Agreed", vehicle_id: "", seller_representative: "", buyer_name: "", buyer_address: "",
  buyer_postcode: "", buyer_phone: "", buyer_email: "", registration: "", vin: "", make: "", model: "", derivative: "", colour: "",
  first_registration: "", mileage: "", mot_expiry: "", keys: "", history: "", data_check: "", road_test: "", price: "", deposit: "",
  deposit_date: "", payment_method: "", collection_date: "", handover_method: "", delivery_mode: "", delivery_address: "", channel: "premises", warranty_included: "false", warranty_details: "",
  special_terms: "", disclosed: "", notes: "", extras: [],
};

export const saleFromRecord = (r) => Object.fromEntries(Object.entries({ ...EMPTY_SALE, ...r }).map(([k, v]) =>
  [k, k === "extras" ? (v || []).map(extraForForm)
    : k === "warranty_included" ? String(!!v) : NUMBER_FIELDS.includes(k) && !v ? "" : v ?? ""]));

const sections = (f) => [
  ["Agreement", [["sale_date", "Date of agreement", "date"], ["status", "Status", "select", SALE_STATUSES], ["seller_representative", "Our representative"],
    ["channel", "How the sale was agreed", "select", [["premises", "In person at our premises"], ["distance", "At a distance (online / phone / email)"]]]]],
  ["Buyer (private individual)", [["buyer_name", "Full name"], ["buyer_phone", "Telephone"], ["buyer_address", "Address (one line per row)", "textarea"],
    ["buyer_postcode", "Postcode"], ["buyer_email", "Email"]]],
  ["Vehicle", [["registration", "Registration"], ["vin", "VIN"], ["make", "Make"], ["model", "Model"], ["derivative", "Derivative / trim"], ["colour", "Colour"],
    ["mileage", "Mileage at sale", "number"], ["keys", "Number of keys", "number"], ["first_registration", "Date of first registration", "date"], ["mot_expiry", "MOT expiry", "date"]]],
  ["History and testing", [["history", "History supplied (e.g. service book, invoices, MOT history)", "textarea"], ["data_check", "Data check (provider and date)"],
    ["road_test", "Road test (date and miles)"]]],
  ["Price and payment", [["price", "Vehicle cash price (£)", "number"], ["deposit", "Deposit paid (£), refundable", "number"], ["deposit_date", "Date deposit paid", "date"],
    ["payment_method", "Payment method"]]],
  ["Warranty", [["warranty_included", "Warranty included in this sale?", "select", [["false", "No: Warranty Wise offered separately"], ["true", "Yes: expressly included"]]],
    ...(f.warranty_included === "true" ? [["warranty_details", "Warranty details (provider, term, claim limit)"]] : [])]],
  ["Printed on the agreement", [["disclosed", "Matters disclosed to the buyer (faults, damage, wear)", "textarea"], ["special_terms", "Additional terms of sale", "textarea"]]],
  ["Notes", [["notes", "Internal notes (not printed)", "textarea"]]],
];

const toBody = (f) => Object.fromEntries(Object.entries(f).map(([k, v]) => [k,
  k === "extras" ? v.map((e) => ({ description: e.description.trim(), value: Number(e.value) || 0, vat: Number(e.vat) || 0, funds_received: !!e.funds_received }))
    : k === "warranty_included" ? v === "true" : NUMBER_FIELDS.includes(k) ? (v === "" ? (OPTIONAL_INTS.includes(k) ? null : 0) : Number(v)) : v]));

const fromVehicle = (v) => ({
  vehicle_id: v.id, registration: v.registration_number || "", vin: v.vin || "", make: v.make || "", model: v.model || "", derivative: v.derivative || "",
  colour: v.colour || "", mileage: v.mileage ?? "", price: v.price ?? "", mot_expiry: /^\d{4}-\d{2}-\d{2}$/.test(v.mot_expiry || "") ? v.mot_expiry : "",
});

export function SaleForm({ initial, saleNo, onSaved, onCancel }) {
  const [f, setF] = useState(initial);
  const [vehicles, setVehicles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [presets, setPresets] = useExtraPresets();
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  // Ticking Delivered adds the usual Delivery extra (with its saved price, if any) unless a delivery extra is already listed.
  const setHandover = (v) => {
    const add = v === "delivered" && !f.extras.some((x) => /deliver/i.test(x.description));
    const preset = presets.find((p) => p.description.trim().toLowerCase() === "delivery");
    setF((p) => ({ ...p, handover_method: v, delivery_mode: v === "delivered" ? p.delivery_mode : "",
      extras: add ? [...p.extras, preset ? extraFromPreset(preset) : { description: "Delivery", value: "", vat: "", funds_received: false }] : p.extras }));
    if (add) toast.success(preset?.value ? "Delivery added to the extras" : "Delivery added to the extras: enter its price");
  };
  useEffect(() => {
    api.get("/admin/vehicles").then((r) => setVehicles(Array.isArray(r.data) ? r.data : [])).catch(() => setVehicles([]));
  }, []);
  const extras = extrasTotal(f.extras);
  const total = (Number(f.price) || 0) + extras;
  const balance = total - (Number(f.deposit) || 0);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.buyer_name.trim() || !f.model.trim() || !(Number(f.price) > 0)) return toast.error("Buyer name, model and price are required");
    if (f.extras.some((x) => !x.description.trim())) return toast.error("Each extra needs a description");
    if (balance < 0) return toast.error("The deposit cannot be more than the total price");
    setBusy(true);
    try {
      const { data } = saleNo ? await api.put(`/admin/sales/${saleNo}`, toBody(f)) : await api.post("/admin/sales", toBody(f));
      toast.success(`Sale agreement ${data.sale_no} saved`);
      onSaved(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="border border-stone bg-paper p-6" data-testid="sale-form">
      <h2 className="mb-5 font-serif text-2xl">{saleNo ? `Edit sale ${saleNo}` : "Record a sale"}</h2>
      <label htmlFor="sale-vehicle-pick" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.1em] text-charcoal/75">Fill vehicle details from stock (optional)</label>
      <select id="sale-vehicle-pick" className="field mb-6 !py-2.5 text-sm" data-testid="sale-vehicle-pick" value={f.vehicle_id}
        onChange={(e) => { const v = vehicles.find((x) => x.id === e.target.value); setF((p) => (v ? { ...p, ...fromVehicle(v) } : { ...p, vehicle_id: "" })); }}>
        <option value="">Not in stock list / enter manually</option>
        {vehicles.map((v) => <option key={v.id} value={v.id}>{[v.year, v.make, v.model, v.derivative].filter(Boolean).join(" ")}</option>)}
      </select>
      {sections(f).map(([title, fields]) => (
        <div key={title}>
          <fieldset className="mb-6">
            <legend className="mb-3 text-sm font-semibold text-forest">{title}</legend>
            <div className="grid gap-4 sm:grid-cols-2">
              {fields.map(([k, l, t, o]) => <Field key={k} k={k} label={l} type={t} options={o} value={f[k]} onChange={set(k)} prefix="sale" />)}
            </div>
          </fieldset>
          {title === "Price and payment" && <SaleExtras extras={f.extras} onChange={set("extras")} presets={presets} setPresets={setPresets} />}
          {title === "Warranty" && <CollectionDelivery f={f} set={set} setHandover={setHandover} />}
        </div>
      ))}
      {Number(f.price) > 0 && <p className="mb-6 border border-stone bg-stone-light p-4 text-sm" data-testid="sale-balance">Vehicle {gbp2(f.price)} · extras {gbp2(extras)} · total {gbp2(total)} · deposit {gbp2(f.deposit)} · <strong>balance due {gbp2(balance)}</strong></p>}
      <div className="flex flex-wrap gap-3">
        <button type="submit" disabled={busy} className="btn btn-primary !py-3" data-testid="sale-save">{busy ? "Saving…" : saleNo ? "Save changes" : "Save sale"}</button>
        <button type="button" onClick={onCancel} className="btn !py-3" data-testid="sale-cancel">Cancel</button>
      </div>
    </form>
  );
}
